import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  Tooltip,
  TablePagination,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  Storage as StorageIcon,
  ExpandMore as ExpandMoreIcon,
  Science as ScienceIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const SteelMaster = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentRecord, setCurrentRecord] = useState({
    steel_code: '',
    steel_name: '',
    steel_grade: '',
    specification: '',
    carbon_min: '',
    carbon_max: '',
    manganese_min: '',
    manganese_max: '',
    phosphorus_min: '',
    phosphorus_max: '',
    sulphur_min: '',
    sulphur_max: '',
    silicon_min: '',
    silicon_max: '',
    chromium_min: '',
    chromium_max: '',
    nickel_min: '',
    nickel_max: '',
    molybdenum_min: '',
    molybdenum_max: '',
    aluminum_min: '',
    aluminum_max: '',
    vanadium_min: '',
    vanadium_max: '',
    titanium_min: '',
    titanium_max: '',
    copper_max: '',
    oxygen_max: '',
    nitrogen_min: '',
    nitrogen_max: '',
    hydrogen_min: '',
    std_reference: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { api } = useAuth();

  useEffect(() => {
    fetchRecords();
  }, []);

  const fetchRecords = async () => {
    try {
      setLoading(true);
      const response = await api.get('/masters/steels');
      setRecords(response.data.data || []);
    } catch (error) {
      console.error('Error fetching steel records:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setCurrentRecord({
      steel_code: '',
      steel_name: '',
      steel_grade: '',
      specification: '',
      carbon_min: '',
      carbon_max: '',
      manganese_min: '',
      manganese_max: '',
      phosphorus_min: '',
      phosphorus_max: '',
      sulphur_min: '',
      sulphur_max: '',
      silicon_min: '',
      silicon_max: '',
      chromium_min: '',
      chromium_max: '',
      nickel_min: '',
      nickel_max: '',
      molybdenum_min: '',
      molybdenum_max: '',
      aluminum_min: '',
      aluminum_max: '',
      vanadium_min: '',
      vanadium_max: '',
      titanium_min: '',
      titanium_max: '',
      copper_max: '',
      oxygen_max: '',
      nitrogen_min: '',
      nitrogen_max: '',
      hydrogen_min: '',
      std_reference: ''
    });
    setEditMode(false);
    setOpenDialog(true);
  };

  const handleEdit = (record) => {
    setCurrentRecord(record);
    setEditMode(true);
    setOpenDialog(true);
  };

  const handleView = (record) => {
    setCurrentRecord(record);
    setOpenViewDialog(true);
  };

  const handleSave = async () => {
    try {
      if (editMode) {
        await api.put(`/masters/steels/${currentRecord.steel_code}`, currentRecord);
      } else {
        await api.post('/masters/steels', currentRecord);
      }
      fetchRecords();
      setOpenDialog(false);
    } catch (error) {
      console.error('Error saving steel record:', error);
    }
  };

  const filteredRecords = records.filter(record =>
    record.steel_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    record.steel_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    record.steel_grade?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    record.specification?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginatedRecords = filteredRecords.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const ChemicalElement = ({ label, min, max, unit = '%' }) => (
    <Grid item xs={12} sm={6} md={4}>
      <TextField
        fullWidth
        label={`${label} Min (${unit})`}
        type="number"
        step="0.0001"
        value={min}
        onChange={(e) => setCurrentRecord({
          ...currentRecord,
          [`${label.toLowerCase().replace(' ', '_')}_min`]: e.target.value
        })}
        size="small"
      />
    </Grid>
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <StorageIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" gutterBottom color="primary">
            Steel Code Master
          </Typography>
        </Box>
        <Typography variant="body1" color="textSecondary">
          Steel specifications and chemical composition management
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Steel Codes
              </Typography>
              <Typography variant="h4">
                {records.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                With Specifications
              </Typography>
              <Typography variant="h4" color="success.main">
                {records.filter(r => r.specification).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Different Grades
              </Typography>
              <Typography variant="h4" color="info.main">
                {new Set(records.map(r => r.steel_grade).filter(Boolean)).size}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Search Results
              </Typography>
              <Typography variant="h4" color="warning.main">
                {filteredRecords.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Actions */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          label="Search steel codes..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
          sx={{ flexGrow: 1, maxWidth: 400 }}
        />
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
        >
          Export
        </Button>
      </Box>

      {/* Data Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Steel Code</strong></TableCell>
                <TableCell><strong>Steel Name</strong></TableCell>
                <TableCell><strong>Grade</strong></TableCell>
                <TableCell><strong>Specification</strong></TableCell>
                <TableCell><strong>Carbon Range</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRecords.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="textSecondary">
                      No steel master records found. Add steel specifications to get started.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedRecords.map((record) => (
                  <TableRow key={record.steel_code} hover>
                    <TableCell>
                      <Chip 
                        label={record.steel_code} 
                        color="primary" 
                        variant="outlined" 
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {record.steel_name || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>{record.steel_grade || '-'}</TableCell>
                    <TableCell>{record.specification || '-'}</TableCell>
                    <TableCell>
                      {record.carbon_min && record.carbon_max
                        ? `${record.carbon_min} - ${record.carbon_max}%`
                        : '-'
                      }
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => handleView(record)}>
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleEdit(record)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredRecords.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="add"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={handleAdd}
      >
        <AddIcon />
      </Fab>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          {editMode ? 'Edit Steel Master' : 'Add New Steel'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom color="primary">
                Basic Information
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Steel Code"
                value={currentRecord.steel_code}
                onChange={(e) => setCurrentRecord({...currentRecord, steel_code: e.target.value})}
                disabled={editMode}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Steel Name"
                value={currentRecord.steel_name}
                onChange={(e) => setCurrentRecord({...currentRecord, steel_name: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Steel Grade"
                value={currentRecord.steel_grade}
                onChange={(e) => setCurrentRecord({...currentRecord, steel_grade: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Specification"
                value={currentRecord.specification}
                onChange={(e) => setCurrentRecord({...currentRecord, specification: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Standard Reference"
                value={currentRecord.std_reference}
                onChange={(e) => setCurrentRecord({...currentRecord, std_reference: e.target.value})}
              />
            </Grid>

            {/* Chemical Composition */}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Divider />
              <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>
                <ScienceIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Chemical Composition
              </Typography>
            </Grid>

            {/* Main Elements */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Carbon Min (%)"
                type="number"
                step="0.001"
                value={currentRecord.carbon_min}
                onChange={(e) => setCurrentRecord({...currentRecord, carbon_min: e.target.value})}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Carbon Max (%)"
                type="number"
                step="0.001"
                value={currentRecord.carbon_max}
                onChange={(e) => setCurrentRecord({...currentRecord, carbon_max: e.target.value})}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Manganese Min (%)"
                type="number"
                step="0.001"
                value={currentRecord.manganese_min}
                onChange={(e) => setCurrentRecord({...currentRecord, manganese_min: e.target.value})}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Manganese Max (%)"
                type="number"
                step="0.001"
                value={currentRecord.manganese_max}
                onChange={(e) => setCurrentRecord({...currentRecord, manganese_max: e.target.value})}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Silicon Min (%)"
                type="number"
                step="0.001"
                value={currentRecord.silicon_min}
                onChange={(e) => setCurrentRecord({...currentRecord, silicon_min: e.target.value})}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Silicon Max (%)"
                type="number"
                step="0.001"
                value={currentRecord.silicon_max}
                onChange={(e) => setCurrentRecord({...currentRecord, silicon_max: e.target.value})}
                size="small"
              />
            </Grid>

            {/* Harmful Elements */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phosphorus Min (%)"
                type="number"
                step="0.001"
                value={currentRecord.phosphorus_min}
                onChange={(e) => setCurrentRecord({...currentRecord, phosphorus_min: e.target.value})}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phosphorus Max (%)"
                type="number"
                step="0.001"
                value={currentRecord.phosphorus_max}
                onChange={(e) => setCurrentRecord({...currentRecord, phosphorus_max: e.target.value})}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Sulphur Min (%)"
                type="number"
                step="0.001"
                value={currentRecord.sulphur_min}
                onChange={(e) => setCurrentRecord({...currentRecord, sulphur_min: e.target.value})}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Sulphur Max (%)"
                type="number"
                step="0.001"
                value={currentRecord.sulphur_max}
                onChange={(e) => setCurrentRecord({...currentRecord, sulphur_max: e.target.value})}
                size="small"
              />
            </Grid>

            {/* Alloying Elements */}
            <Grid item xs={12}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>Alloying Elements</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Chromium Min (%)"
                        type="number"
                        step="0.001"
                        value={currentRecord.chromium_min}
                        onChange={(e) => setCurrentRecord({...currentRecord, chromium_min: e.target.value})}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Chromium Max (%)"
                        type="number"
                        step="0.001"
                        value={currentRecord.chromium_max}
                        onChange={(e) => setCurrentRecord({...currentRecord, chromium_max: e.target.value})}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Nickel Min (%)"
                        type="number"
                        step="0.001"
                        value={currentRecord.nickel_min}
                        onChange={(e) => setCurrentRecord({...currentRecord, nickel_min: e.target.value})}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Nickel Max (%)"
                        type="number"
                        step="0.001"
                        value={currentRecord.nickel_max}
                        onChange={(e) => setCurrentRecord({...currentRecord, nickel_max: e.target.value})}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Molybdenum Min (%)"
                        type="number"
                        step="0.001"
                        value={currentRecord.molybdenum_min}
                        onChange={(e) => setCurrentRecord({...currentRecord, molybdenum_min: e.target.value})}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Molybdenum Max (%)"
                        type="number"
                        step="0.001"
                        value={currentRecord.molybdenum_max}
                        onChange={(e) => setCurrentRecord({...currentRecord, molybdenum_max: e.target.value})}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Vanadium Min (%)"
                        type="number"
                        step="0.001"
                        value={currentRecord.vanadium_min}
                        onChange={(e) => setCurrentRecord({...currentRecord, vanadium_min: e.target.value})}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Vanadium Max (%)"
                        type="number"
                        step="0.001"
                        value={currentRecord.vanadium_max}
                        onChange={(e) => setCurrentRecord({...currentRecord, vanadium_max: e.target.value})}
                        size="small"
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editMode ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={openViewDialog} onClose={() => setOpenViewDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <StorageIcon />
            Steel Specification Details
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom color="primary">
              {currentRecord.steel_code} - {currentRecord.steel_name}
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Grade</Typography>
                <Typography variant="body2">{currentRecord.steel_grade || '-'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Specification</Typography>
                <Typography variant="body2">{currentRecord.specification || '-'}</Typography>
              </Grid>
              
              {(currentRecord.carbon_min || currentRecord.carbon_max) && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="primary" sx={{ mt: 2 }}>
                    Chemical Composition
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    {currentRecord.carbon_min && (
                      <Typography variant="body2">
                        Carbon: {currentRecord.carbon_min}% - {currentRecord.carbon_max}%
                      </Typography>
                    )}
                    {currentRecord.manganese_min && (
                      <Typography variant="body2">
                        Manganese: {currentRecord.manganese_min}% - {currentRecord.manganese_max}%
                      </Typography>
                    )}
                    {currentRecord.silicon_min && (
                      <Typography variant="body2">
                        Silicon: {currentRecord.silicon_min}% - {currentRecord.silicon_max}%
                      </Typography>
                    )}
                    {currentRecord.phosphorus_max && (
                      <Typography variant="body2">
                        Phosphorus: Max {currentRecord.phosphorus_max}%
                      </Typography>
                    )}
                    {currentRecord.sulphur_max && (
                      <Typography variant="body2">
                        Sulphur: Max {currentRecord.sulphur_max}%
                      </Typography>
                    )}
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenViewDialog(false)}>Close</Button>
          <Button 
            onClick={() => {
              setOpenViewDialog(false);
              handleEdit(currentRecord);
            }} 
            variant="contained"
          >
            Edit
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SteelMaster;